import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { formatDueDate } from '../utils/relativeTime';
import { useAuth } from '../context/AuthContext';

// Vazifalar - ikki model bitta jadvalda (backend/app/models/task.py):
// - Model P (shaxsiy): assigned_by_id yo'q, faqat egasi ko'radi/tahrirlaydi.
// - Model A (tayinlangan): boshliq/admin/direktor xodimga vazifa tayinlaydi,
//   xodim qabul/rad qiladi. Xavfsizlik chegarasi TO'LIQ serverda
//   (routers/tasks.py) - bu sahifa faqat backend view= parametri qaytargan
//   narsani ko'rsatadi, hech narsani o'zi "yashirmaydi" yoki hisoblamaydi.
type TaskStatus = 'personal' | 'pending' | 'accepted' | 'rejected' | 'done';

interface Task {
  id: number;
  title: string;
  description?: string | null;
  due_date?: string | null;
  priority: 'low' | 'medium' | 'high';
  done: boolean;
  done_at?: string | null;
  created_at: string;
  status: TaskStatus;
  rejection_reason?: string | null;
  owner_id: number;
  owner_name?: string | null;
  assigned_by_id?: number | null;
  assigned_by_name?: string | null;
}

interface AssignableUser {
  id: number;
  full_name: string;
  role: string;
}

const emptyForm = { title: '', description: '', due_date: '', priority: 'medium' as Task['priority'], assignedTo: '' };

const PRIORITY_LABEL: Record<string, { label: string; color: string }> = {
  low: { label: 'Past', color: 'bg-gray-100 text-gray-600' },
  medium: { label: "O'rta", color: 'bg-blue-100 text-blue-700' },
  high: { label: 'Yuqori', color: 'bg-red-100 text-red-700' },
};

const STATUS_BADGE: Record<TaskStatus, { label: string; color: string }> = {
  personal: { label: 'Shaxsiy', color: 'bg-gray-100 text-gray-500' },
  pending: { label: 'Javob kutilmoqda', color: 'bg-orange-100 text-orange-700' },
  accepted: { label: 'Qabul qilindi', color: 'bg-blue-100 text-blue-700' },
  rejected: { label: 'Rad etildi', color: 'bg-red-100 text-red-700' },
  done: { label: 'Bajarildi', color: 'bg-green-100 text-green-700' },
};

type MineFilter = 'active' | 'done' | 'all';
type Tab = 'mine' | 'inbox' | 'assigned';

const DueLabel: React.FC<{ dueDate?: string | null; done: boolean }> = ({ dueDate, done }) => {
  if (!dueDate || done) return null;
  const due = formatDueDate(dueDate, 'uz');
  return (
    <p className={`text-xs mt-1 ${due.overdue ? 'text-red-500 font-medium' : due.today ? 'text-orange-500 font-medium' : 'text-gray-400'}`}>
      📅 {due.text}
    </p>
  );
};

const Tasks: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('mine');
  const [mineTasks, setMineTasks] = useState<Task[]>([]);
  const [inboxTasks, setInboxTasks] = useState<Task[]>([]);
  const [assignedTasks, setAssignedTasks] = useState<Task[]>([]);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [mineFilter, setMineFilter] = useState<MineFilter>('active');

  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState<Task | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [mineRes, inboxRes, assignedRes] = await Promise.all([
        api.get('/tasks', { params: { view: 'mine' } }),
        api.get('/tasks', { params: { view: 'inbox' } }),
        api.get('/tasks', { params: { view: 'assigned_overview' } }),
      ]);
      setMineTasks(mineRes.data);
      setInboxTasks(inboxRes.data);
      setAssignedTasks(assignedRes.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchAll();
    api.get('/tasks/assignable-users').then(res => setAssignableUsers(res.data)).catch(() => {});
  }, [fetchAll]);

  const canAssign = assignableUsers.length > 0;

  const mineFiltered = mineTasks.filter(t => {
    if (mineFilter === 'active') return !t.done;
    if (mineFilter === 'done') return t.done;
    return true;
  });

  const openAdd = () => { setForm(emptyForm); setEditTask(null); setError(''); setShowModal(true); };
  const openEdit = (t: Task) => {
    setForm({
      title: t.title,
      description: t.description || '',
      due_date: t.due_date || '',
      priority: t.priority,
      assignedTo: '',
    });
    setEditTask(t); setError(''); setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { setError('Sarlavha kiritilishi kerak'); return; }
    setLoading(true); setError('');
    const payload: Record<string, unknown> = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      due_date: form.due_date || null,
      priority: form.priority,
    };
    if (!editTask && form.assignedTo) {
      payload.assigned_to_id = Number(form.assignedTo);
    }
    try {
      if (editTask) await api.put(`/tasks/${editTask.id}`, payload);
      else await api.post('/tasks', payload);
      setShowModal(false);
      fetchAll();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Xatolik yuz berdi');
    } finally { setLoading(false); }
  };

  const toggleDone = async (t: Task) => {
    setMineTasks(prev => prev.map(x => x.id === t.id ? { ...x, done: !x.done } : x));
    try {
      await api.put(`/tasks/${t.id}`, { done: !t.done });
    } catch (e) {
      console.error(e);
      fetchAll();
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Vazifani o'chirishni tasdiqlaysizmi?")) return;
    try {
      await api.delete(`/tasks/${id}`);
      fetchAll();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Xatolik yuz berdi');
    }
  };

  const acceptTask = async (id: number) => {
    setInboxTasks(prev => prev.filter(t => t.id !== id));
    try {
      await api.put(`/tasks/${id}/accept`);
      fetchAll();
    } catch (e) {
      console.error(e);
      fetchAll();
    }
  };

  const submitReject = async () => {
    if (rejectingId == null || !rejectReason.trim()) return;
    setLoading(true);
    try {
      await api.put(`/tasks/${rejectingId}/reject`, { reason: rejectReason.trim() });
      setRejectingId(null);
      setRejectReason('');
      fetchAll();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Xatolik yuz berdi');
    } finally { setLoading(false); }
  };

  const f = (key: keyof typeof form, val: string) => setForm({ ...form, [key]: val });

  const activeCount = mineTasks.filter(t => !t.done).length;
  const doneCount = mineTasks.filter(t => t.done).length;

  const tabs: { key: Tab; label: string; count?: number }[] = [
    { key: 'mine', label: 'Mening vazifalarim' },
    { key: 'inbox', label: 'Menga tayinlangan', count: inboxTasks.length },
  ];
  if (canAssign || assignedTasks.length > 0) {
    tabs.push({ key: 'assigned', label: 'Men tayinlaganlarim', count: assignedTasks.length });
  }

  return (
    <div className="p-3 md:p-6">
      <div className="flex justify-between items-center mb-4 md:mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-800">Vazifalar</h2>
          <p className="text-xs md:text-sm text-gray-400">Shaxsiy eslatmalar va tayinlangan vazifalar</p>
        </div>
        <button onClick={openAdd}
          className="px-3 md:px-4 py-2 rounded-lg text-sm text-white bg-blue-600 hover:bg-blue-700">
          + Qo'shish
        </button>
      </div>

      {/* Tab'lar */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition flex items-center gap-1.5 ${tab === t.key ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border hover:bg-gray-50'}`}>
            {t.label}
            {!!t.count && (
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${tab === t.key ? 'bg-white/20' : 'bg-orange-100 text-orange-700'}`}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* ===== "Mening vazifalarim" ===== */}
      {tab === 'mine' && (
        <>
          <div className="flex gap-2 mb-4">
            {([
              { key: 'active', label: `Faol (${activeCount})` },
              { key: 'done', label: `Bajarilgan (${doneCount})` },
              { key: 'all', label: `Hammasi (${mineTasks.length})` },
            ] as { key: MineFilter; label: string }[]).map(t => (
              <button key={t.key} onClick={() => setMineFilter(t.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${mineFilter === t.key ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 border hover:bg-gray-50'}`}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-xl shadow divide-y divide-gray-100">
            {mineFiltered.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm">
                {mineFilter === 'active' ? "Bajarilmagan vazifalar yo'q" : mineFilter === 'done' ? "Bajarilgan vazifalar yo'q" : "Vazifalar yo'q"}
              </div>
            ) : mineFiltered.map(t => {
              const isPersonal = !t.assigned_by_id;
              return (
                <div key={t.id} className="flex items-start gap-3 p-4">
                  <input
                    type="checkbox"
                    checked={t.done}
                    onChange={() => toggleDone(t)}
                    className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0 cursor-pointer"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className={`text-sm font-medium break-words ${t.done ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{t.title}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_LABEL[t.priority].color}`}>
                        {PRIORITY_LABEL[t.priority].label}
                      </span>
                      {!isPersonal && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[t.status].color}`}>
                          {STATUS_BADGE[t.status].label}
                        </span>
                      )}
                    </div>
                    {!isPersonal && t.assigned_by_name && (
                      <p className="text-xs text-gray-400 mt-0.5">{t.assigned_by_name} tayinladi</p>
                    )}
                    {t.description && (
                      <p className="text-sm text-gray-500 mt-1 break-words">{t.description}</p>
                    )}
                    {t.status === 'rejected' && t.rejection_reason && (
                      <p className="text-xs text-red-500 mt-1">Sabab: {t.rejection_reason}</p>
                    )}
                    <DueLabel dueDate={t.due_date} done={t.done} />
                  </div>
                  {isPersonal && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => openEdit(t)} className="px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs">✏️</button>
                      <button onClick={() => handleDelete(t.id)} className="px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs">🗑️</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ===== "Menga tayinlangan" (javob kutilmoqda) ===== */}
      {tab === 'inbox' && (
        <div className="bg-white rounded-xl shadow divide-y divide-gray-100">
          {inboxTasks.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">Javob kutayotgan vazifalar yo'q</div>
          ) : inboxTasks.map(t => (
            <div key={t.id} className="p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${t.priority === 'high' ? 'bg-red-500' : t.priority === 'medium' ? 'bg-blue-500' : 'bg-gray-400'}`} />
                <p className="text-sm font-medium text-gray-800 break-words">{t.title}</p>
              </div>
              {t.assigned_by_name && <p className="text-xs text-gray-400 mt-0.5">{t.assigned_by_name} tayinladi</p>}
              {t.description && <p className="text-sm text-gray-500 mt-1 break-words">{t.description}</p>}
              <DueLabel dueDate={t.due_date} done={false} />

              {rejectingId === t.id ? (
                <div className="mt-3 space-y-2">
                  <input
                    autoFocus
                    type="text"
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    placeholder="Rad etish sababi..."
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-red-400"
                  />
                  <div className="flex gap-2">
                    <button onClick={submitReject} disabled={loading || !rejectReason.trim()}
                      className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 disabled:opacity-50">
                      Rad etishni tasdiqlash
                    </button>
                    <button onClick={() => { setRejectingId(null); setRejectReason(''); }}
                      className="px-3 py-1.5 text-gray-400 text-xs hover:text-gray-600">
                      Bekor
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 mt-3">
                  <button onClick={() => acceptTask(t.id)}
                    className="px-3 py-1.5 bg-green-100 text-green-700 text-xs rounded-lg hover:bg-green-200 font-medium">
                    ✓ Qabul qilish
                  </button>
                  <button onClick={() => { setRejectingId(t.id); setRejectReason(''); }}
                    className="px-3 py-1.5 bg-red-50 text-red-600 text-xs rounded-lg hover:bg-red-100 font-medium">
                    ✕ Rad etish
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ===== "Men tayinlaganlarim" (+ boshliqlar uchun bo'lim nazorati) ===== */}
      {tab === 'assigned' && (
        <div className="bg-white rounded-xl shadow divide-y divide-gray-100">
          {assignedTasks.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">Tayinlangan vazifalar yo'q</div>
          ) : assignedTasks.map(t => (
            <div key={t.id} className="flex items-start gap-3 p-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className={`text-sm font-medium break-words ${t.done ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{t.title}</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[t.status].color}`}>
                    {STATUS_BADGE[t.status].label}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  {t.owner_name} uchun{t.assigned_by_name ? ` — ${t.assigned_by_name} tayinlagan` : ''}
                </p>
                {t.description && <p className="text-sm text-gray-500 mt-1 break-words">{t.description}</p>}
                {t.status === 'rejected' && t.rejection_reason && (
                  <p className="text-xs text-red-500 mt-1">Sabab: {t.rejection_reason}</p>
                )}
                <DueLabel dueDate={t.due_date} done={t.done} />
              </div>
              {/* Faqat tayinlovchining o'zi o'chira oladi - boshqa boshliq/direktor
                  kuzatuvi (bo'lim/kompaniya nazorati) READ-ONLY, backend ham
                  buni majburlaydi (403 qaytaradi). */}
              {t.assigned_by_id === user?.id && (
                <button onClick={() => handleDelete(t.id)}
                  className="px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs flex-shrink-0">🗑️</button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-xl w-full md:max-w-lg p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg md:text-xl font-bold mb-4">{editTask ? 'Vazifani tahrirlash' : 'Yangi vazifa'}</h3>
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}
            <div className="space-y-3 md:space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sarlavha *</label>
                <input value={form.title} onChange={e => f('title', e.target.value)} autoFocus
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Izoh</label>
                <textarea value={form.description} onChange={e => f('description', e.target.value)} rows={3}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Muddat</label>
                  <input type="date" value={form.due_date} onChange={e => f('due_date', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Muhimlik</label>
                  <select value={form.priority} onChange={e => f('priority', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="low">Past</option>
                    <option value="medium">O'rta</option>
                    <option value="high">Yuqori</option>
                  </select>
                </div>
              </div>
              {/* Tayinlash - faqat yaratishda, faqat ruxsat etilgan userlar ro'yxati
                  bo'sh bo'lmasa ko'rinadi. Ro'yxatning o'zi ham backendda cheklangan
                  (boshliq - o'z bo'limi, admin/direktor - hamma), lekin haqiqiy
                  majburlash BARIBIR serverda (POST /tasks) - bu shunchaki UI qulayligi. */}
              {!editTask && canAssign && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kimga tayinlash</label>
                  <select value={form.assignedTo} onChange={e => f('assignedTo', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">O'zim uchun (shaxsiy)</option>
                    {assignableUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.full_name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-4 md:mt-6">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Bekor qilish</button>
              <button onClick={handleSubmit} disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">
                {loading ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tasks;
